/**
 * LA FOUILLE — ce qu'il y a à ouvrir ici, et ce qu'on y prend (§3.6, design §3).
 *
 * ELLE VIT DANS LA ZONE DE DÉCISION, sous les choix et séparée d'eux par un filet. C'est le bon
 * endroit et ce n'est pas un compromis : fouiller est une décision au même titre que franchir une
 * porte — cela coûte des heures, et l'horloge tue —, mais ce n'est pas un chemin. Les mélanger aux
 * choix ferait cliquer sur « fouiller le corps » en croyant partir.
 *
 * UNE CACHE FERMÉE NE DIT PAS CE QU'ELLE CONTIENT. Le serveur ne l'envoie pas (`contenu: null`), et
 * l'interface n'a donc rien à cacher : c'est la même règle que le brouillard de la carte, tenue là
 * où elle compte — un joueur qui verrait le contenu d'un coffre avant de l'ouvrir n'aurait plus de
 * raison de payer l'heure qu'il coûte.
 *
 * ON PREND LIGNE PAR LIGNE, ET ON N'EST PAS OBLIGÉ. Chaque pile a son bouton ; ce qu'on laisse reste
 * écrit là, et la cache attend. C'est ce qui fait du poids (§3.3) un arbitrage et non une punition :
 * devant un sac plein, la question est « quoi laisser », pas « qu'est-ce que j'ai perdu ».
 */
import { useState } from "react";

import type { CacheEnVue, NoeudEnVue, ObjetConnu } from "../../api/types.js";
import { Glyphe } from "../../composants/Glyphe.js";
import { CACHES, dureeDite, nomDObjet, poidsDit } from "../../lexique/mots.js";
import { t } from "../../lexique/langue.js";

function Cache({
  cache,
  catalogue,
  bloque,
  surFouille,
  surPrends
}: {
  cache: CacheEnVue;
  catalogue: Map<string, ObjetConnu>;
  bloque: boolean;
  surFouille: (cacheId: string, reponse?: string) => void;
  surPrends: (cacheId: string, objetId: string) => void;
}) {
  const mot = CACHES[cache.espece] ?? CACHES.sol!;
  const [reponse, poseLaReponse] = useState("");

  /* UNE ÉNIGME SE RÉPOND, ELLE NE SE FOUILLE PAS (§3.6). D'où un champ et non un bouton seul : la
     question est déjà écrite dans le libellé — c'est le libellé —, et l'on tape le mot dessous. Un
     seul mot, jamais une phrase : le serveur ignore la casse, les accents et l'article, ce qui rend
     le champ tolérant sans le rendre bavard. */
  if (!cache.ouverte && cache.enigme) {
    return (
      <form
        className="cache cache--enigme"
        onSubmit={(e) => {
          e.preventDefault();
          if (reponse.trim().length > 0) surFouille(cache.id, reponse.trim());
        }}
      >
        <div className="cache__tete">
          <Glyphe nom="enigme" taille={15} />
          <span className="cache__libelle">{cache.libelle}</span>
        </div>
        <div className="rang rang--serre">
          <input
            className="champ champ--reponse"
            type="text"
            value={reponse}
            maxLength={40}
            placeholder={t("un mot")}
            aria-label={t("Votre réponse à l'énigme")}
            onChange={(e) => poseLaReponse(e.target.value)}
            disabled={bloque}
          />
          <button type="submit" className="puce" disabled={bloque || reponse.trim().length === 0}>
            {t("Répondre")}
          </button>
        </div>
      </form>
    );
  }

  if (!cache.ouverte) {
    return (
      <button
        type="button"
        className="cache cache--fermee"
        // VERROUILLÉE, LE BOUTON RESTE LISIBLE et se laisse presser : le refus du serveur dit où
        // chercher la clef, ce qu'un bouton éteint n'aurait jamais appris (design §3). C'est aussi
        // pour cela que la serrure s'affiche au lieu de se cacher — on la note, on revient (§4.2).
        disabled={bloque}
        onClick={() => surFouille(cache.id)}
        title={
          cache.verrouillee
            ? t("Fermé : il vous faut {quoi}", { quoi: cache.clefManquante ?? t("une clef") })
            : cache.dissimulee
              ? t("Personne ne l'avait vue")
              : undefined
        }
      >
        <span className="cache__tete">
          <Glyphe nom={mot.glyphe} taille={15} />
          <span className="cache__libelle">{cache.libelle}</span>
        </span>
        <span className="cache__mentions">
          {cache.verrouillee ? (
            <span className="mention mention--verrou">
              <Glyphe nom="clef" taille={13} /> {t("il vous faut {quoi}", { quoi: cache.clefManquante ?? t("une clef") })}
            </span>
          ) : (
            <span className="mention">
              <Glyphe nom="main" taille={13} /> {mot.verbe}
            </span>
          )}
          {cache.coutHeures > 0 && !cache.verrouillee ? (
            <span className="mention mention--temps">
              <Glyphe nom="horloge" taille={13} /> {dureeDite(cache.coutHeures)}
            </span>
          ) : null}
        </span>
      </button>
    );
  }

  const contenu = cache.contenu ?? [];
  return (
    <div className="cache cache--ouverte">
      <div className="cache__tete">
        <Glyphe nom={mot.glyphe} taille={15} />
        <span className="cache__libelle">{cache.libelle}</span>
      </div>
      <ul className="cache__contenu">
        {contenu.map((pile) => {
          const objet = catalogue.get(pile.objetId);
          return (
            <li key={pile.objetId}>
              <span>
                {nomDObjet(pile.objetId, catalogue)}
                {pile.quantite > 1 ? <span className="nombre faible"> {t("×")}{pile.quantite}</span> : null}
                {objet ? <span className="faible"> — {poidsDit(objet.poids)}</span> : null}
              </span>
              <button type="button" className="puce" disabled={bloque} onClick={() => surPrends(cache.id, pile.objetId)}>
                {t("Prendre")}
              </button>
            </li>
          );
        })}
        {!contenu.length ? <li className="faible">{mot.vide}</li> : null}
      </ul>
      {contenu.length ? (
        <p className="faible cache__laisse">{t("Ce que vous laissez reste ici. Rien ne s'évapore.")}</p>
      ) : null}
    </div>
  );
}

export function LaFouille({
  noeud,
  catalogue,
  bloque,
  surFouille,
  surPrends,
  surFouilleLesLieux
}: {
  noeud: NoeudEnVue;
  catalogue: Map<string, ObjetConnu>;
  bloque: boolean;
  surFouille: (cacheId: string, reponse?: string) => void;
  surPrends: (cacheId: string, objetId: string) => void;
  surFouilleLesLieux: () => void;
}) {
  return (
    <section className="fouille" aria-label={t("Ce qu'il y a à fouiller ici")}>
      {noeud.caches.map((cache) => (
        <Cache
          key={cache.id}
          cache={cache}
          catalogue={catalogue}
          bloque={bloque}
          surFouille={surFouille}
          surPrends={surPrends}
        />
      ))}

      {/* ELLE NE PROMET RIEN, et c'est exprès. Une action qui n'apparaîtrait qu'aux nœuds à secret
          chercherait à la place du joueur : sa seule présence dirait « il y a quelque chose ici ». */}
      {noeud.lieuxFouilles ? (
        <p className="faible fouille__fait">{t("Vous avez fouillé les lieux.")}</p>
      ) : (
        <button type="button" className="puce puce--sourde fouille__lieux" disabled={bloque} onClick={surFouilleLesLieux}>
          <Glyphe nom="main" taille={13} /> {t("Fouiller les lieux")}
          <span className="faible"> — {dureeDite(1)}</span>
        </button>
      )}
    </section>
  );
}
